/**
 * bleScale — lectura de básculas inteligentes por Web Bluetooth (BLE estándar).
 *
 * Regla de negocio: la báscula solo llena los parámetros NECESARIOS que la app
 * evalúa. Si el dispositivo expone más datos de los que necesitamos, se ignoran;
 * si expone menos, los faltantes quedan vacíos para carga manual. Por eso
 * `normalizeScaleMetrics` mapea/filtra el payload crudo del BLE al conjunto
 * canónico y descarta todo lo demás (ej. BMI, BMR).
 */

// BLE Scale Service UUIDs (Bluetooth SIG standard)
const WEIGHT_SCALE_SERVICE = 0x181D;
const BODY_COMPOSITION_SERVICE = 0x181B;
const WEIGHT_MEASUREMENT_CHAR = 0x2A9D;
const BODY_COMPOSITION_CHAR = 0x2A9C;

export const isBluetoothSupported = () =>
  typeof navigator !== 'undefined' && !!navigator.bluetooth;

// Parse weight measurement from BLE characteristic (0x2A9D)
const parseWeightMeasurement = (dataView) => {
  const flags = dataView.getUint8(0);
  const isImperial = (flags & 0x01) !== 0;
  const hasTimestamp = (flags & 0x02) !== 0;
  const hasUserId = (flags & 0x04) !== 0;
  const hasBMI = (flags & 0x08) !== 0;

  let offset = 1;
  const weightRaw = dataView.getUint16(offset, true);
  offset += 2;

  let weightKg = isImperial ? weightRaw * 0.01 * 0.453592 : weightRaw * 0.005;
  weightKg = Math.round(weightKg * 10) / 10;

  const result = { weight_kg: weightKg };

  if (hasTimestamp) offset += 7;
  if (hasUserId) offset += 1;
  if (hasBMI && offset + 2 <= dataView.byteLength) {
    result.bmi = Math.round(dataView.getUint16(offset, true) * 0.1 * 10) / 10;
    offset += 2;
  }
  return result;
};

// Parse body composition from BLE characteristic (0x2A9C)
const parseBodyComposition = (dataView) => {
  const flags = dataView.getUint16(0, true);
  let offset = 2;
  const result = {};

  if (flags & 0x02 && offset + 2 <= dataView.byteLength) {
    result.body_fat_percent = Math.round(dataView.getUint16(offset, true) * 0.1 * 10) / 10;
    offset += 2;
  }
  if (flags & 0x04 && offset + 2 <= dataView.byteLength) {
    result.bmr_kcal = dataView.getUint16(offset, true);
    offset += 2;
  }
  if (flags & 0x08 && offset + 2 <= dataView.byteLength) {
    result.muscle_percent = Math.round(dataView.getUint16(offset, true) * 0.1 * 10) / 10;
    offset += 2;
  }
  return result;
};

/**
 * Mapea el payload crudo del BLE al conjunto de métricas canónicas (metric_key
 * del catálogo). Solo devuelve las claves NECESARIAS que se pudieron derivar;
 * cualquier dato extra (BMI, BMR, etc.) se descarta a propósito.
 */
export const normalizeScaleMetrics = (raw) => {
  const out = {};
  if (raw.weight_kg != null) out.weight_kg = raw.weight_kg;
  if (raw.body_fat_percent != null) out.body_fat_pct = raw.body_fat_percent;
  // Masa muscular en kg a partir del % que reporta la báscula.
  if (raw.muscle_percent != null && raw.weight_kg) {
    out.muscle_mass_kg = Math.round((raw.weight_kg * raw.muscle_percent) / 100 * 10) / 10;
  }
  return out;
};

/**
 * Conecta a una báscula BLE y notifica métricas normalizadas conforme llegan.
 *
 * @param {Object} opts
 * @param {(metrics: Object) => void} opts.onMetrics  Recibe solo claves canónicas.
 * @param {() => void} [opts.onDisconnect]
 * @returns {Promise<{ device: any, disconnect: () => void }>}
 */
export const connectScale = async ({ onMetrics, onDisconnect } = {}) => {
  if (!isBluetoothSupported()) {
    const err = new Error('Web Bluetooth no disponible');
    err.code = 'NO_BLUETOOTH';
    throw err;
  }

  const device = await navigator.bluetooth.requestDevice({
    filters: [
      { services: [WEIGHT_SCALE_SERVICE] },
      { services: [BODY_COMPOSITION_SERVICE] },
      { namePrefix: 'MI' },
      { namePrefix: 'Renpho' },
      { namePrefix: 'eufy' },
      { namePrefix: 'Wyze' },
      { namePrefix: 'Scale' },
    ],
    optionalServices: [WEIGHT_SCALE_SERVICE, BODY_COMPOSITION_SERVICE, 'battery_service'],
  });

  // Acumula lo crudo entre notificaciones (peso y composición llegan aparte).
  const raw = {};
  const emit = () => onMetrics && onMetrics(normalizeScaleMetrics(raw));

  if (onDisconnect) {
    device.addEventListener('gattserverdisconnected', onDisconnect);
  }

  const server = await device.gatt.connect();

  try {
    const weightService = await server.getPrimaryService(WEIGHT_SCALE_SERVICE);
    const weightChar = await weightService.getCharacteristic(WEIGHT_MEASUREMENT_CHAR);
    await weightChar.startNotifications();
    weightChar.addEventListener('characteristicvaluechanged', (e) => {
      try {
        Object.assign(raw, parseWeightMeasurement(e.target.value));
        emit();
      } catch (_) { /* payload no estándar: ignorar */ }
    });
  } catch (_) { /* servicio de peso no disponible */ }

  try {
    const bodyService = await server.getPrimaryService(BODY_COMPOSITION_SERVICE);
    const bodyChar = await bodyService.getCharacteristic(BODY_COMPOSITION_CHAR);
    await bodyChar.startNotifications();
    bodyChar.addEventListener('characteristicvaluechanged', (e) => {
      try {
        Object.assign(raw, parseBodyComposition(e.target.value));
        emit();
      } catch (_) { /* payload no estándar: ignorar */ }
    });
  } catch (_) { /* servicio de composición no disponible */ }

  return {
    device,
    disconnect: () => {
      if (device.gatt?.connected) device.gatt.disconnect();
    },
  };
};
